"use client";
import type { ReactElement } from "react";
import { formatDateInZone, formatTimeInZone, formatDuration } from "@/lib/timezones";
import type { BookResponse } from "@/lib/types";

// Keyed on the backend's `meetingPlatform` token, which is the narrower of the
// two namespaces: CalendarServiceImpl.setMeetingPlatform writes "googlemeet",
// "zoom", "microsoftteams" and "other", and SeeThePro rooms are tagged
// "seethepro" off their meetingKey. NOT the provider-app namespace, where Teams
// is "msteams" — the two differ only for Teams, which is how "teams" got in here
// and why every Teams booking has been falling through to the generic label.
// `other` is deliberately ABSENT from the map below. It means "we did not
// recognise this conferencing URL" (kris-backend#10044), so there is no
// product to name — the `?? "Join Meeting"` fallback is already the right
// answer, and naming it would claim knowledge the backend disclaims.
const PLATFORM_LABELS: Record<string, string> = {
  googlemeet: "Join with Google Meet",
  microsoftteams: "Join with Microsoft Teams",
  zoom: "Join with Zoom",
  seethepro: "Join with SeeThePro",
};

type Props = {
  bookResponse: BookResponse;
  timezone: string;
  durationMinutes: number;
};

export function BookingConfirmation({ bookResponse, timezone, durationMinutes }: Props): ReactElement {
  // manageUrl comes from the backend already encoded. It used to be rebuilt
  // here from orgId + token against NEXT_PUBLIC_APP_ORIGIN — a variable set in
  // no deployed config, so its fallback sent every lead who booked outside
  // production to the production host, where their token does not exist.
  const { meetingTitle, startTime, endTime, meetingUrl, meetingPlatform, manageUrl } = bookResponse;

  const dateLabel = formatDateInZone(startTime, timezone);
  const startLabel = formatTimeInZone(startTime, timezone);
  const endLabel = formatTimeInZone(endTime, timezone);
  const durationLabel = formatDuration(durationMinutes);

  return (
    <div className="flex flex-col items-center py-6 text-center">
      {/* Checkmark */}
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F9F9] shadow-sm">
        <svg className="h-8 w-8 text-[#029391]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h2 className="mb-1 text-xl font-bold text-neutral-900">This meeting is scheduled</h2>
      <p className="mb-7 text-sm text-neutral-500">
        You&apos;ll receive a calendar invite with all the details.
      </p>

      {/* Meeting time card */}
      <div className="mb-6 w-full rounded-2xl border border-[#d0f0ef] bg-[#E8F9F9] px-6 py-5 text-left">
        {meetingTitle && (
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-400">{meetingTitle}</p>
        )}
        {dateLabel && <p className="text-sm font-medium text-neutral-600">{dateLabel}</p>}
        <p className="mt-0.5 text-xl font-bold text-neutral-900">
          {startLabel}{endLabel ? ` – ${endLabel}` : ""}
        </p>
        <div className="mt-3 flex items-center gap-4 text-xs text-neutral-400">
          <span className="flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {durationLabel}
          </span>
          <span className="flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
            {timezone.replaceAll("_", " ")}
          </span>
        </div>
      </div>

      {/* Join button */}
      {meetingUrl && meetingPlatform && (
        <a
          href={meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#029391] px-6 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-[#027a78] hover:shadow-lg transition-all"
        >
          {/* Generic label, deliberately NOT the raw token. CalendarEventCanvasBody
              falls back to the token itself and the two should stay different:
              that canvas is seller-facing, where "microsoftteams" is debuggable
              signal, while this is the last screen a lead sees and a raw token
              on it reads as a bug. Don't harmonise them. */}
          {PLATFORM_LABELS[meetingPlatform] ?? "Join Meeting"}
        </a>
      )}

      <a href={manageUrl} className="text-sm font-medium text-[#029391] hover:underline">
        Manage Booking →
      </a>
    </div>
  );
}
